import {
  getIncentiveItemEligibilityError,
  normalizeIncentiveItemEligibility,
} from './IncentiveSettings.itemEligibility';
import {
  DynamicFee,
  FeeAppliesTo,
  FeeChargeBasis,
  FeeConditionField,
  FeeConditionOperator,
  FeeEligibility,
  FeeEligibilityCondition,
  FeeEligibilityScope,
  FeeType,
  IncentiveFlowStage,
  IncentiveOfferType,
  IncentiveRule,
  IncentiveSettings,
  IncentiveValueType,
  RefundOptimizationRule,
} from 'types/incentive';
import { generateId } from 'utils';
import { normalizeAdditionalFeeAmount } from './IncentiveSettings.fees';
import {
  normalizeIncentiveRules,
  normalizeOptimizationRules,
  normalizePercentageValue,
} from './IncentiveSettings.percentages';

export const DEFAULT_INCENTIVE_FLOW_ORDER = [
  IncentiveFlowStage.RETURN_DEFLECTION,
  IncentiveFlowStage.REFUND_OPTIMIZATION,
];

export type DynamicIncentiveFee = Omit<
  DynamicFee,
  'appliesTo' | 'enabled' | 'chargeBasis' | 'eligibility'
> & {
  appliesTo: FeeAppliesTo;
  enabled: boolean;
  chargeBasis: FeeChargeBasis;
  eligibility: FeeEligibility;
};

export type IncentiveSettingsFormState = {
  deflectionEnabled: boolean;
  rules: IncentiveRule[];
  refundOptimizationEnabled: boolean;
  optimizationRules: RefundOptimizationRule[];
  flowOrder: IncentiveFlowStage[];
  smartEligibility: boolean;
  allowAdditionalFees: boolean;
  waiveFeesForProtectedOrders: boolean;
  dynamicFees: DynamicIncentiveFee[];
};

export const normalizeIncentiveFlowOrder = (
  flowOrder?: IncentiveFlowStage[],
) => {
  const uniqueStages = (flowOrder || []).filter((stage, index, stages) => {
    return (
      DEFAULT_INCENTIVE_FLOW_ORDER.includes(stage) &&
      stages.indexOf(stage) === index
    );
  });

  return [
    ...uniqueStages,
    ...DEFAULT_INCENTIVE_FLOW_ORDER.filter(
      (stage) => !uniqueStages.includes(stage),
    ),
  ];
};

const isPositiveFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const feeChargeBases = Object.values(FeeChargeBasis);
const feeEligibilityScopes = Object.values(FeeEligibilityScope);
const feeConditionOperators = Object.values(FeeConditionOperator);

const normalizeFeeCondition = (
  condition: FeeEligibilityCondition,
  createId: () => string,
): FeeEligibilityCondition => ({
  id: condition?.id || createId(),
  field:
    condition?.field === FeeConditionField.SKU
      ? condition.field
      : FeeConditionField.SKU,
  operator: feeConditionOperators.includes(condition?.operator)
    ? condition.operator
    : FeeConditionOperator.EQUALS,
  value: typeof condition?.value === 'string' ? condition.value : '',
});

const normalizeFeeEligibility = (
  eligibility: FeeEligibility | undefined,
  createId: () => string,
): FeeEligibility => ({
  scope: feeEligibilityScopes.includes(eligibility?.scope as FeeEligibilityScope)
    ? (eligibility?.scope as FeeEligibilityScope)
    : FeeEligibilityScope.ALL_RETURNS,
  conditions: Array.isArray(eligibility?.conditions)
    ? (eligibility?.conditions || []).map((condition) =>
        normalizeFeeCondition(condition, createId),
      )
    : [],
});

export const getIncentiveSettingsFormState = (
  settings?: IncentiveSettings | null,
  createId: () => string = generateId,
): IncentiveSettingsFormState => {
  const returnDeflection = settings?.returnDeflection;
  const refundOptimization = settings?.refundOptimization;
  const storedDeflectionRules = returnDeflection?.rules;
  const storedOptimizationRules = refundOptimization?.rules;
  const legacyKeepItemIncentive = returnDeflection?.keepItemIncentive;
  const legacyKeepItemValue = legacyKeepItemIncentive?.value;
  const legacyStoreCreditBonus = refundOptimization?.storeCreditBonus;
  const storedDynamicFees = settings?.refundFallback?.dynamicFees;
  let rules: IncentiveRule[] = [];
  let optimizationRules: RefundOptimizationRule[] = [];

  if (Array.isArray(storedDeflectionRules)) {
    rules = normalizeIncentiveRules(storedDeflectionRules).map((rule) => ({
      ...rule,
      isEnabled: rule.isEnabled !== false,
    }));
  } else if (isPositiveFiniteNumber(legacyKeepItemValue)) {
    rules = [
      {
        id: createId(),
        name: 'Legacy keep-item rule',
        minItemValue: 0,
        requiresProtection: false,
        isEnabled: true,
        eligibility: normalizeIncentiveItemEligibility(),
        options: [
          {
            id: createId(),
            offerType:
              legacyKeepItemIncentive?.offerType ||
              IncentiveOfferType.STORE_CREDIT,
            value: normalizePercentageValue(legacyKeepItemValue),
            valueType: IncentiveValueType.PERCENTAGE,
          },
        ],
      },
    ];
  }

  if (Array.isArray(storedOptimizationRules)) {
    optimizationRules = normalizeOptimizationRules(storedOptimizationRules).map(
      (rule) => ({
        ...rule,
        isEnabled: rule.isEnabled !== false,
      }),
    );
  } else if (isPositiveFiniteNumber(legacyStoreCreditBonus)) {
    optimizationRules = [
      {
        id: createId(),
        name: 'Legacy store-credit rule',
        minItemValue: 0,
        requiresProtection: false,
        isEnabled: true,
        bonusValue: normalizePercentageValue(legacyStoreCreditBonus),
      },
    ];
  }

  return {
    deflectionEnabled: returnDeflection
      ? returnDeflection.enabled ?? true
      : false,
    rules,
    refundOptimizationEnabled: refundOptimization
      ? refundOptimization.enabled ?? true
      : false,
    optimizationRules,
    flowOrder: normalizeIncentiveFlowOrder(settings?.flowOrder),
    smartEligibility: settings?.bounds?.smartEligibilityCheck ?? false,
    allowAdditionalFees: settings?.refundFallback?.allowAdditionalFees ?? false,
    waiveFeesForProtectedOrders:
      settings?.refundFallback?.waiveFeesForProtectedOrders ?? false,
    dynamicFees: Array.isArray(storedDynamicFees)
      ? storedDynamicFees.map((fee) => ({
          ...fee,
          amount: normalizeAdditionalFeeAmount(fee.amount, fee.feeType),
          appliesTo: fee.appliesTo || FeeAppliesTo.REFUND,
          enabled: fee.enabled !== false,
          chargeBasis: feeChargeBases.includes(
            fee.chargeBasis as FeeChargeBasis,
          )
            ? (fee.chargeBasis as FeeChargeBasis)
            : FeeChargeBasis.PER_RETURN,
          eligibility: normalizeFeeEligibility(fee.eligibility, createId),
        }))
      : [],
  };
};

type IncentiveValidationInput = Pick<
  IncentiveSettingsFormState,
  | 'deflectionEnabled'
  | 'rules'
  | 'refundOptimizationEnabled'
  | 'optimizationRules'
  | 'allowAdditionalFees'
> & { dynamicFees: DynamicFee[] };

const toFormNumber = (value: unknown) => {
  if (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return Number.NaN;
  }

  return Number(value);
};

const getRangeValidationError = (
  ruleLabel: string,
  minItemValue: unknown,
  maxItemValue?: unknown,
) => {
  const min = toFormNumber(minItemValue);
  if (!Number.isFinite(min) || min < 0) {
    return `${ruleLabel} minimum return value must be 0 or greater.`;
  }

  if (
    maxItemValue !== undefined &&
    maxItemValue !== null &&
    String(maxItemValue).trim() !== ''
  ) {
    const max = toFormNumber(maxItemValue);
    if (!Number.isFinite(max) || max < min) {
      return `${ruleLabel} maximum return value must be at least the minimum return value.`;
    }
  }

  return null;
};

const trimmedName = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

export const getIncentiveSettingsValidationError = ({
  deflectionEnabled,
  rules,
  refundOptimizationEnabled,
  optimizationRules,
  allowAdditionalFees,
  dynamicFees,
}: IncentiveValidationInput): string | null => {
  if (deflectionEnabled) {
    for (let index = 0; index < rules.length; index += 1) {
      const rule = rules[index];
      if (rule.isEnabled === false) continue;
      const ruleLabel = trimmedName(rule.name) || `Keep-item rule ${index + 1}`;
      const eligibilityError = getIncentiveItemEligibilityError(
        rule.eligibility,
        ruleLabel,
      );
      if (eligibilityError) return eligibilityError;
      const rangeError = getRangeValidationError(
        ruleLabel,
        rule.minItemValue,
        rule.maxItemValue,
      );
      if (rangeError) return rangeError;
      if (!Array.isArray(rule.options) || rule.options.length === 0) {
        return `${ruleLabel} needs at least one customer offer.`;
      }

      for (const option of rule.options) {
        const value = toFormNumber(option.value);
        if (!Number.isFinite(value) || value < 0) {
          return `${ruleLabel} offer values must be 0 or greater.`;
        }
        if (option.valueType === IncentiveValueType.PERCENTAGE && value > 100) {
          return `${ruleLabel} percentage offers must be between 0 and 100.`;
        }
      }
    }
  }

  if (refundOptimizationEnabled) {
    for (let index = 0; index < optimizationRules.length; index += 1) {
      const rule = optimizationRules[index];
      if (rule.isEnabled === false) continue;
      const ruleLabel =
        trimmedName(rule.name) || `Store-credit rule ${index + 1}`;
      const rangeError = getRangeValidationError(
        ruleLabel,
        rule.minItemValue,
        rule.maxItemValue,
      );
      if (rangeError) return rangeError;
      const bonus = toFormNumber(rule.bonusValue);
      if (!Number.isFinite(bonus) || bonus < 0 || bonus > 100) {
        return `${ruleLabel} bonus must be between 0 and 100.`;
      }
    }
  }

  if (allowAdditionalFees) {
    for (let index = 0; index < dynamicFees.length; index += 1) {
      const fee = dynamicFees[index];
      if (fee.enabled === false) continue;
      const feeName = trimmedName(fee.name);
      if (!feeName) {
        return `Fee ${index + 1} needs a name.`;
      }
      const amount = toFormNumber(fee.amount);
      if (!Number.isFinite(amount) || amount < 0) {
        return `${feeName} amount must be 0 or greater.`;
      }
      if (fee.feeType === FeeType.PERCENTAGE && amount > 100) {
        return `${feeName} percentage must be between 0 and 100.`;
      }
      const eligibilityScope =
        fee.eligibility?.scope || FeeEligibilityScope.ALL_RETURNS;
      const eligibilityConditions = fee.eligibility?.conditions || [];
      if (
        fee.chargeBasis === FeeChargeBasis.PER_MATCHING_UNIT &&
        eligibilityScope !== FeeEligibilityScope.MATCHING_ITEMS
      ) {
        return `${feeName} can charge per matching unit only when matching items are selected.`;
      }
      if (eligibilityScope === FeeEligibilityScope.MATCHING_ITEMS) {
        if (eligibilityConditions.length === 0) {
          return `${feeName} needs at least one SKU condition.`;
        }

        for (
          let conditionIndex = 0;
          conditionIndex < eligibilityConditions.length;
          conditionIndex += 1
        ) {
          const condition = eligibilityConditions[conditionIndex];
          if (!trimmedName(condition.value)) {
            return `${feeName} SKU condition ${
              conditionIndex + 1
            } needs a value.`;
          }
        }
      }
    }
  }

  return null;
};
